import { supabaseClient } from "@/lib/database/supabase";
import { Player, Run, SeasonWithAssociations } from "@shared/lib/database/query.types";
import { QueryData } from "@supabase/supabase-js";
import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import useAuthStore from "./useAuthStore";

function createSeasonsQuery (seasonIds: Array<string>) {
  // TODO: only fetch current seasons
  return supabaseClient
    .from("seasons")
    .select("*, season_regulars(player_id, player:players(id, name, theme, avatar_storage_path, supporter_type)), crew:crews!inner(*), court:courts!inner(*), runs(*)")
    .in("id", seasonIds)
    .is("completed_at", null);
}

export type SeasonForHome = QueryData<ReturnType<typeof createSeasonsQuery>>[0];

export default defineStore("SeasonStore", () => {
  const homeSeasonIds = ref<Set<string>>(new Set());
  const {
    currentPlayer,
  } = storeToRefs(useAuthStore());

  async function fetchHomeSeasons (): Promise<[Error | null, Array<SeasonForHome> | null]> {
    if (!currentPlayer.value) return [new Error("No current player"), null];

    const {
      data: seasons,
      error: seasonsError,
    } = await supabaseClient.rpc("get_seasons_for_player", {
      player_id: currentPlayer.value.id,
    });

    if (seasonsError) return [seasonsError, null];

    const {
      data,
      error,
    } = await createSeasonsQuery(seasons.map((season) => season.id))
      .order("start_on", {
        ascending: true,
      })
      .order("start_at", {
        referencedTable: "runs",
        ascending: true,
      });

    if (data) {
      homeSeasonIds.value = new Set(data.map((season) => season.id));
      data.forEach((season) => {
        seasonMap.value.set(season.id, season);
      });
    }

    return [error, data];
  }

  const seasonMap = ref<Map<string, SeasonForHome | SeasonWithAssociations | null>>(new Map());

  const homeSeasons = computed(() => {
    return Array.from(homeSeasonIds.value).reduce<Array<SeasonForHome>>((acc, id) => {
      const season = seasonMap.value.get(id);
      if (!season || !!season.completed_at) return acc;

      acc.push(season as SeasonForHome);

      return acc;
    }, []);
  });

  async function fetchSeason (id: string) {
    const {
      data: season,
    } = await supabaseClient
      .from("seasons")
      .select("*, crew:crews!inner(*, players(id, name, theme, avatar_storage_path, supporter_type)), court:courts!inner(*), runs(*), season_regulars(player_id, player:players(id, name, theme, avatar_storage_path, supporter_type))")
      .eq("id", id)
      .maybeSingle()
      .throwOnError();

    seasonMap.value.set(id, season);
    return season;
  }

  function getSeason (id: string) {
    return seasonMap.value.get(id) || null;
  }

  function updateSeason (id: string, season: Partial<SeasonForHome | SeasonWithAssociations>) {
    const existingSeason = seasonMap.value.get(id);
    if (!existingSeason) return;

    seasonMap.value.set(id, {
      ...existingSeason,
      ...season,
      season_regulars: existingSeason.season_regulars.map((seasonRegular) => ({
        ...seasonRegular,
        player: seasonRegular.player || null,
      })),
    });

    if (!season.completed_at) {
      homeSeasonIds.value.add(id);
    } else {
      homeSeasonIds.value.delete(id);
    }

    return existingSeason;
  }

  function removeSeason (id: string) {
    seasonMap.value.delete(id);
  }

  function updateSeasonRegulars (seasonId: string, players: Array<Player>) {
    const season = getSeason(seasonId);
    if (!season) return;

    season.season_regulars = players.map((player) => ({
      season_id: seasonId,
      player_id: player.id,
      player,
    }));
  }

  function updateSeasonRun (seasonId: string, runId: string, run: Partial<Run>) {
    const season = getSeason(seasonId);
    if (!season) return;

    const existingRun = season.runs.find((run) => run.id === runId);
    if (!existingRun) return;

    season.runs.splice(season.runs.indexOf(existingRun), 1, {
      ...existingRun,
      ...run,
    });

    return existingRun;
  }

  function removeSeasonRun (seasonId: string, runId: string) {
    const season = getSeason(seasonId);
    if (!season) return;

    season.runs = season.runs.filter((run) => run.id !== runId);
  }

  return {
    homeSeasons,
    fetchHomeSeasons,
    updateSeasonRun,
    removeSeasonRun,
    fetchSeason,
    getSeason,
    updateSeason,
    removeSeason,
    updateSeasonRegulars,
  };
});
